import React, { useEffect, useState, Suspense, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FetchAddressServiceInit } from '../../../store/ManageAddress/FetchAddress/FetchAddressSlice'
import './ManageAddress.css';
import ManageAddressSliderView from './ManageAddressSliderView';
import AddressCard from "../../../components/DisplayMyAccount/AddressCard";
import AddAddressForm from "../../MyDevices/components/AddDevice/AddAddress";
import { Modal, ModalHeader } from "reactstrap";
import { ReactComponent as ModalCloseIcon } from "../../../assets/images/mydevices/modal-close-icon.svg";
import Loader from "../../../components/shared/loader/Loader";
import useAddressValidation from "../../../utils/CustomHooks/useAddressValidation";
import UsePostalCodeValidation from "../../../utils/CustomHooks/UsePostalCodeValidation";
import { ResetUpdateShippingAddress, UpdateShippingAddress } from '../../../actions/Dashboard';
import { getUserDataByKey } from "../../../utils/Authentication/handleUserSession";
import HeaderComponent from "../../../components/shared/header/HeaderComponent/HeaderComponent";
import LoaderSuccessMessagesInPopup from "../../../components/LoaderSuccessMessagesInPopup/LoaderSuccessMessagesInPopup";
import { ManageSubscription, ManageSubscriptionResetState } from "../../../actions/MyAccount";
import { SUBSCRIPTION_TYPE, USER_ENROLLED_SERVICE_LIST } from "../../../constants/AppConstants";
import { useTranslation } from 'react-i18next';
import { UpdateShippingAddressRequest, UpdateShippingAddressReset } from "../../../store/brotherPlus/UpdateShippingAddress/UpdateShippingAddressSlice";

const ManageAddress = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation(["translation"]);
    const [address, setAddress] = useState({});
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [errors, setErrors] = useState();
    const [addressUpdateState, setAddressUpdateState] = useState(false);
    const [isBillingAddress, setIsBillingAddress] = useState(false);
    const [isEcoProShippingAddressUpdate, setIsEcoProShippingAddressUpdate] = useState(false);
    const {
        fetchAddress_loading,
        fetchAddress_data
    } = useSelector(state => state.ROOTREDUCER_RTK.fetchAddressService);

    const updateBrotherPlusShippingAddressResponse = useSelector(state => state.BROTHERPLUS.updateShippingAddressService);
    const updateEcoProShippingAddressResponse = useSelector(state => state.UpdateShipping);
    const ManageSubscriptionResponse = useSelector(state => state.ManageSubscription);
    const {
        validatePostalCode
    } = UsePostalCodeValidation(address?.country, address?.postCode);

    const requiredAddressFields = ['addressName', 'addressLine1', 'cityTown', 'country', 'postCode'];
    const { validateAddress } = useAddressValidation(address, requiredAddressFields);

    let deviceAddressList = fetchAddress_data?.deviceAddress;
    let billingAddress = fetchAddress_data?.billingAddress;

    useEffect(() => {
        setAddressUpdateState(true);
        if (updateEcoProShippingAddressResponse.success || updateBrotherPlusShippingAddressResponse.success) {
            setTimeout(() => {
                setModalIsOpen(false);

                // get user shipping and billing address data
                dispatch(FetchAddressServiceInit());

                // reset EcoPro update shipping address state
                dispatch(ResetUpdateShippingAddress());

                // reset BrotherPlus update shipping address state
                dispatch(UpdateShippingAddressReset());
            }, 3000);
        }
    }, [dispatch, updateBrotherPlusShippingAddressResponse.success, updateEcoProShippingAddressResponse.success]);

    useEffect(() => {
        // get user shipping and billing address data
        dispatch(FetchAddressServiceInit());
    }, [dispatch]);

    useEffect(() => {
        setAddressUpdateState(true);
        let timer;
        if (ManageSubscriptionResponse.success) {
            timer = setTimeout(() => {
                setModalIsOpen(false);

                // reset manageSubscription state
                dispatch(ManageSubscriptionResetState());

                // get user shipping and billing address data
                dispatch(FetchAddressServiceInit());
            }, 3000);
        }
        return () => clearTimeout(timer);
    }, [dispatch, ManageSubscriptionResponse])

    // build payload to update EcoPro shipping address
    const subscriptionPayload = {
        accountDetails: {
            shippingAddress: {
                address: address.addressLine1,
                addressName: address.addressName,
                addressLine2: address.addressLine2,
                city: address.cityTown,
                country: address.country,
                postcode: address.postCode,
                "company": "",
                "province": ""
            },
            "personalDetails": {
                "firstName": getUserDataByKey("firstName", 'personal_details'),
                "lastName": getUserDataByKey("lastName", 'personal_details'),
                "emailAddress": getUserDataByKey("emailAddress", 'personal_details')
            },
            device: {
                deviceId: address.deviceId,
            }
        }
    };

    // build payload to update user billing address
    const billingAddressPayload = {
        accountDetails: {
            "personalDetails": {
                "firstName": getUserDataByKey("firstName", 'personal_details'),
                "lastName": getUserDataByKey("lastName", 'personal_details'),
                "emailAddress": getUserDataByKey("emailAddress", 'personal_details')
            },
            billingAddress: {
                address: address.addressLine1,
                addressLine2: address.addressLine2 || "",
                city: address.cityTown,
                province: "",
                postcode: address.postCode,
                AddressNumber: "",
                country: address.country,
                addressName: address.addressName
            }
        }
    }

    const handleContinue = async () => {
        const validationErrors = await validateAddress();
        const isPostalcodeNotValid = await validatePostalCode();
        if (isPostalcodeNotValid) {
            validationErrors['postCode'] = t("FORM_CONTENT.FORM_ERROR.POSTCODE_VALID")
        }
        if (Object.keys(validationErrors).length === 0) {
            setErrors();
            // call Manage API to update billing address
            if (isBillingAddress) {
                dispatch(ManageSubscription(paymentMethodId, billingAddressPayload, SUBSCRIPTION_TYPE.UPDATE_BILLING));
            }
            // call EcoPro UpdateShippingAddress API if device is enrolled to only Ecopro or to both EcoPro & BrotherPlus
            else if (address.enrolledServices.toLowerCase().includes(USER_ENROLLED_SERVICE_LIST?.ECOPRO.toLowerCase())) {
                dispatch(UpdateShippingAddress(subscriptionPayload));
            }
            // call BrotherPlus UpdateShippingAddress API if device is enrolled only to BrotherPlus
            else dispatch(UpdateShippingAddressRequest(address));
        } else {
            setErrors(validationErrors);
        }
    }

    const toggleModal = () => {
        setModalIsOpen((prev) => !prev);
    };

    const handleUpdateAddress = useCallback((address) => {
        setAddress(address)
        setModalIsOpen(!modalIsOpen);
        setAddressUpdateState(false);
        setErrors();
    }, [modalIsOpen]);

    const paymentMethodId = "";

    return (
        <div class='manage-address-container'>
            {fetchAddress_loading && <Loader />}
            {fetchAddress_data && <>
                <HeaderComponent title={t("MANAGE_ADDRESS.HEADER.TITLE")} description={t("MANAGE_ADDRESS.HEADER.DESCRIPTION")}> </HeaderComponent>
                <div className="title-header">
                    <div className="title-text"> {t('MANAGE_ADDRESS.SHIPPING_ADDRESS_HEADER')} </div>
                </div>
                {
                    !deviceAddressList.length && <p className="info-error-message">
                        {t("ERROR_CONTENT.NO_DATA")}
                    </p>
                }
                <ManageAddressSliderView addressList={deviceAddressList}
                    onUpdateAddress={(address) => {
                        handleUpdateAddress(address);
                        setIsBillingAddress(false);
                        address.enrolledServices.toLowerCase().includes(USER_ENROLLED_SERVICE_LIST?.ECOPRO.toLowerCase())
                            ? setIsEcoProShippingAddressUpdate(true)
                            : setIsEcoProShippingAddressUpdate(false);
                    }} >
                </ManageAddressSliderView>

                <div className="title-header mt-30">
                    <div className="title-text"> {t('MANAGE_ADDRESS.BILLING_ADDRESS_HEADER')} </div>
                </div>
                {
                    billingAddress === null && <p className="info-error-message">
                        {t("ERROR_CONTENT.NO_DATA")}
                    </p>
                }
                {billingAddress !== null &&
                    <AddressCard
                        addressType='BILLING'
                        address={billingAddress}
                        index='0'
                        updateAddress={(address) => { handleUpdateAddress(address); setIsBillingAddress(true) }}
                    ></AddressCard>
                }

                <Modal isOpen={modalIsOpen} className="my-devices-add-device-modal-container manage-address-modal" fullscreen>
                    <div className="modal-close-icon">
                        <ModalHeader close={<ModalCloseIcon onClick={() => toggleModal()} />}></ModalHeader>
                    </div>
                    <div className="add-device-modal-content-wrapper">
                        <Suspense >
                            {<AddAddressForm
                                handleContinue={handleContinue}
                                setAddress={setAddress}
                                address={address}
                                errors={errors}
                                showChooseExistingText={false}
                                isBillingAddress={isBillingAddress}
                            ></AddAddressForm>}
                        </Suspense>
                    </div>
                    {addressUpdateState &&
                        <LoaderSuccessMessagesInPopup
                            Response={isBillingAddress
                                ? ManageSubscriptionResponse
                                : isEcoProShippingAddressUpdate
                                    ? updateEcoProShippingAddressResponse
                                    : updateBrotherPlusShippingAddressResponse}
                            successMsg={t("MANAGE_ADDRESS.ADDRESS_UPDATE_SUCCESS")}
                            showLoader={false}
                        />}
                </Modal>
            </>}
        </div>
    )
}

export default ManageAddress;